/**
 * The sample estate the allocation engine plans over, and the plan it
 * starts from.
 *
 * DEMONSTRATION records. The stands are the sample stand register (101–121
 * contact with their own gates, the remote row with the R1/R2 exclusion
 * pair, the freight and GA stands); gates 1–21 belong to those stands and
 * B1–B3 are bus gates; three check-in islands of twelve desks; five belts,
 * two of them long enough for a widebody. The rules are examples of the kind
 * an airport writes — SV on its own pier and island — marked as samples.
 *
 * The stand plan the day starts from is the sample stand plan itself, with
 * its double-booked 104 and the delay running into 107, so the engine's
 * first job on screen is the job it exists for.
 */
import type { Assignment, Learned, Policy, Resource, ResourceKind, Rule } from "../domain/allocation";
import { DEFAULT_POLICIES } from "../domain/allocation";
import type { Demand } from "../domain/allocation";
import type { StandRegister } from "../domain/stands";

export function mockResources(register: StandRegister): Resource[] {
  const out: Resource[] = [];
  for (const s of register.stands) {
    out.push({
      kind: "position",
      code: s.code,
      name: s.name,
      group: s.zoneCode,
      maxSize: s.maxSize,
      servedInPlace: Boolean(s.bridge),
      uses:
        s.kind === "cargo" ? ["cargo"] : s.kind === "ga" ? ["ga"] : s.kind === "maintenance" ? ["maintenance"] : ["pax", "cargo", "ferry", "positioning"],
      excludes: s.excludes,
      links: s.gate ? [s.gate] : undefined,
      closed: s.outOfService ? [{ from: s.outOfService.from, to: s.outOfService.to, why: s.outOfService.why }] : undefined,
    });
  }
  /* the west remote apron: two widebody stands opened for the pilgrim season, planned here only */
  for (const code of ["W1", "W2"])
    out.push({ kind: "position", code, name: `West ${code.slice(1)}`, group: "APRON-W", maxSize: "E", servedInPlace: false, uses: ["pax", "cargo", "ferry", "positioning"] });
  for (let g = 1; g <= 21; g++) out.push({ kind: "gate", code: String(g), name: `Gate ${g}`, group: g <= 10 ? "North pier" : "South pier", seq: g });
  for (const [i, code] of ["B1", "B2", "B3"].entries())
    out.push({ kind: "gate", code, name: `Bus gate ${code}`, group: "Bus lounge", seq: 30 + i, bus: true });
  for (const [gi, island] of ["A", "B", "C"].entries())
    for (let k = 1; k <= 12; k++) {
      const n = gi * 12 + k;
      out.push({ kind: "counter", code: `C${String(n).padStart(2, "0")}`, name: `Desk ${n}`, group: island, seq: n });
    }
  for (let b = 1; b <= 5; b++)
    out.push({ kind: "belt", code: `Belt ${b}`, name: `Belt ${b}`, group: "Arrivals hall", seq: b, maxSize: b >= 4 ? "F" : "D" });
  return out;
}

export function mockRules(): Rule[] {
  return [
    {
      id: "rule-1",
      kind: "position",
      operator: "SV",
      codes: ["101", "102", "103", "104", "105", "106", "107", "108", "109", "110"],
      effect: "prefer",
      weight: 20,
      note: "SAMPLE — Saudia on the north pier, beside its lounge",
    },
    { id: "rule-2", kind: "counter", operator: "SV", codes: ["@A"], effect: "prefer", weight: 30, note: "SAMPLE — Saudia checks in on island A" },
    { id: "rule-3", kind: "counter", operator: "XY", codes: ["@B"], effect: "prefer", weight: 30, note: "SAMPLE — flynas checks in on island B" },
    { id: "rule-4", kind: "position", serviceType: "cargo", codes: ["F1"], effect: "prefer", weight: 40, note: "SAMPLE — freighters on the freight stand" },
    {
      id: "rule-5",
      kind: "position",
      sizes: ["C"],
      codes: ["106", "107"],
      effect: "avoid",
      weight: 15,
      note: "SAMPLE — keep the two code-E contact stands for widebodies",
    },
  ];
}

export function mockLearned(): Learned[] {
  return [{ kind: "position", operator: "XY", code: "102", n: 3 }];
}

export function mockPolicies(): Record<ResourceKind, Policy> {
  return structuredClone(DEFAULT_POLICIES);
}

/** The stand plan as it stands, turned into engine assignments. */
export function assignmentsFromStands(register: StandRegister, demands: readonly Demand[]): Assignment[] {
  const out: Assignment[] = [];
  for (const a of register.assignments) {
    const d = demands.find((x) => x.kind === "position" && x.movementIds.includes(a.flightId));
    if (!d || out.some((o) => o.key === d.key)) continue;
    out.push({ key: d.key, kind: "position", segments: [{ codes: [a.standCode], from: d.from, to: d.to }], source: "plan" });
  }
  return out;
}
